import { useState, useMemo, useCallback, useEffect } from 'react';
import { Property, AppUser, UserRole } from '../types';
import { generateMaskedCode } from '../utils/propertyMappers';
import { ALLOWED_REFERRAL_PROPERTY_TYPES } from '../constants';
import { getResolvedCommissionRate } from '../utils/commissionUtils';
import { getPropertyByIdApi, searchByCodesApi } from '../services/propertyApiClient';
import { softDeletePropertyApi } from '../services/adminPropertyApiClient';

const DELETE_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbzpvhskWL7oFiIwYWSAIIp3ODrDYkJMpoQBv8g-7KxoZODfA0WDuLgnkTKhczWQ3tD-/exec';

export const getYoutubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : null;
};

export const usePropertyDetailLogic = (
  property: Property,
  appUser: AppUser | null,
  inmobiliariasConfig: Record<string, any>,
  onPropertyUpdate?: (id: string, updates: Partial<Property>) => void
) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLocallyDeleted, setIsLocallyDeleted] = useState(property.status === 'Eliminado');
  const [resolvedProperty, setResolvedProperty] = useState<Property>(property);
  const [isLoadingResolved, setIsLoadingResolved] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchUpdatedData = async () => {
      if (!property?.id && !property?.codigo) return;
      const isUpdated = property.status === 'Actualizado' || property.status === 'ActualizadoCodigo';

      try {
        if (isUpdated && property.codigo_actual) {
          setIsLoadingResolved(true);
          const response = await searchByCodesApi([property.codigo_actual.trim()]);
          if (isMounted && response?.items?.length > 0) {
            setResolvedProperty(response.items[0]);
            return;
          }
        }

        if (property.id) {
          const freshData = await getPropertyByIdApi(property.id);
          if (isMounted && freshData) {
            setResolvedProperty(freshData);
          }
        }
      } catch (e) {
        console.warn('[usePropertyDetailLogic] Error fetching from backend API:', e);
        if (isMounted) setResolvedProperty(property);
      } finally {
        if (isMounted) setIsLoadingResolved(false);
      }
    };

    fetchUpdatedData();
    return () => {
      isMounted = false;
    };
  }, [property]);

  const p = resolvedProperty;
  const isDeleted = isLocallyDeleted || p.status === 'Eliminado';
  const canDelete = appUser && [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OPERACION, UserRole.COMERCIAL_INTERNO].includes(appUser.role);

  const displayCode = useMemo(() => {
    if (appUser?.role === UserRole.BROKER_PREMIUM) return p.codigo_enmascarado || generateMaskedCode(p.id);
    return p.codigo;
  }, [p, appUser]);

  const canReferCredit = useMemo(() => ALLOWED_REFERRAL_PROPERTY_TYPES.includes(p.tipoinmueble), [p.tipoinmueble]);

  const media = useMemo(() => {
    const imgs: string[] = p.images || [];
    const video = p.video_youtube_url ? { embedUrl: getYoutubeEmbedUrl(p.video_youtube_url) } : null;
    return { validImages: imgs.filter((img) => typeof img === 'string' && !getYoutubeEmbedUrl(img)), mainVideo: video };
  }, [p]);

  const { resolvedRate, resolvedValue } = useMemo(() => {
    const rate = getResolvedCommissionRate(p, appUser, inmobiliariasConfig);
    return { resolvedRate: rate, resolvedValue: p.precio_venta * rate };
  }, [p, appUser, inmobiliariasConfig]);

  const handleSoftDelete = useCallback(
    async (nombre: string, razon: string) => {
      setIsDeleting(true);
      try {
        await fetch(DELETE_WEBHOOK_URL, {
          method: 'POST',
          body: JSON.stringify({
            nombre_comercial: nombre,
            razon,
            codigo_inmueble: p.codigo,
            estatus_eliminacion: 'Eliminado',
            owner: p.owner,
            codigo_brp: p.codigo_brp_o_inmobiliaria || p.codigo_brp || null,
          }),
        }).catch(() => null);

        await softDeletePropertyApi(p.id);
        setIsLocallyDeleted(true);
        if (onPropertyUpdate) onPropertyUpdate(p.id, { status: 'Eliminado' });
        return true;
      } catch {
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [p, onPropertyUpdate]
  );

  return {
    resolvedProperty: p,
    isLoadingResolved,
    isDeleted,
    canDelete,
    displayCode,
    media,
    resolvedRate,
    resolvedValue,
    isDeleting,
    handleSoftDelete,
    canReferCredit,
  };
};
